import type { DisciplineAttachment, HabitDisciplineAction } from '../types';

export type NourishmentLens = 'hydration' | 'nutrition';

/** What an action shows of itself to the rule below — any action has these, or neither. */
export interface LensTagged {
  discipline?: DisciplineAttachment;
  disciplineAction?: HabitDisciplineAction;
}

/**
 * ⭐ WHICH NOURISHMENT LENS an action tagged to the `nutrition` discipline belongs to
 * (owner, 2026-10-17 — each lens's Actions list only its own: Hydration "Drink water",
 * Nutrition "Eat my meals").
 *   1. What it DOES wins: a habit linked to hydration (`disciplineAction` 'hydration')
 *      is Hydration's; the linked eating habit ('nutrition') is Nutrition's.
 *   2. Then its tag, `discipline.subAreaId` — the lens's own Add sets it.
 *   3. Anything else is Nutrition's: the discipline IS `nutrition`, and an older action
 *      without a tag must stay visible on one lens rather than vanish from both.
 * ⚠️ Supplements is its own discipline (`supplements`) with its own panel.
 */
export function nourishmentLensOf(a: LensTagged): NourishmentLens {
  const type = a.disciplineAction?.type;
  if (type === 'hydration' || type === 'nutrition') return type;
  return a.discipline?.subAreaId === 'hydration' ? 'hydration' : 'nutrition';
}

/** `AreaActionsPanel`'s `subArea` for each lens — stable objects (memo dependencies). */
export const NOURISHMENT_LENS_ACTIONS: Record<NourishmentLens, { id: NourishmentLens; of: (a: LensTagged) => string }> = {
  hydration: { id: 'hydration', of: nourishmentLensOf },
  nutrition: { id: 'nutrition', of: nourishmentLensOf },
};

/* ── Re-tagging from the Area field (owner, 2026-10-17) ── */

/**
 * ⚠️⚠️ NOURISHMENT IS STORED UNDER THREE IDS. The Area field tags it `nourishment` (the
 * area's id); Hydration, the eating habit and the lens panels write `nutrition` (the
 * old discipline's id); the Supplements lens's panel writes `supplements`. Ranks don't
 * care (Nourishment is store-backed), but the panels list by id — so every place that
 * reads "is this Nourishment?" asks here.
 */
export const NOURISHMENT_AREA_ID = 'nourishment';
const NOURISHMENT_IDS: readonly string[] = [NOURISHMENT_AREA_ID, 'nutrition', 'supplements'];
/** The ids the Hydration and Nutrition lens panels list. */
export const FOOD_AND_WATER_IDS: readonly string[] = ['nutrition', NOURISHMENT_AREA_ID];

export const isNourishmentId = (id: string | undefined): boolean => !!id && NOURISHMENT_IDS.includes(id);

/**
 * ⭐ A one-shot hand-off: the lens Nourishment opens on (owner, 2026-10-18 — a habit's
 * "Sub-area: Nutrition" link). Written just before the page opens, read and REMOVED by
 * Nourishment when it mounts, like `breathingLens`. ⚠️ A storage key: never rename it.
 */
export const NOURISHMENT_LENS_KEY = 'nourishmentLens';

export type NourishmentPart = NourishmentLens | 'supplements';

/**
 * The lens an action shows on — the supplements habit's link first (what it DOES wins, as for
 * the other two — 2026-10-23), then Supplements by its id, else `nourishmentLensOf`.
 */
export function nourishmentPartOf(a: LensTagged): NourishmentPart {
  if (a.disciplineAction?.type === 'supplements') return 'supplements';
  return a.discipline?.disciplineId === 'supplements' ? 'supplements' : nourishmentLensOf(a);
}

/**
 * The same attachment, moved to a lens. Hydration / Nutrition keep the area id the
 * action already had (Supplements' own id is left for Nourishment's); Supplements is
 * its own discipline, so it takes that id and no tag.
 */
export function moveToPart(value: DisciplineAttachment, part: NourishmentPart): DisciplineAttachment {
  if (part === 'supplements') return { ...value, disciplineId: 'supplements', subAreaId: undefined };
  return { ...value, disciplineId: value.disciplineId === 'supplements' ? NOURISHMENT_AREA_ID : value.disciplineId, subAreaId: part };
}

/** A habit whose LINK decides its lens — the field can't move it (the link would win). */
export function lensLinkOf(action: HabitDisciplineAction | undefined): NourishmentPart | null {
  return action?.type === 'hydration' || action?.type === 'nutrition' || action?.type === 'supplements' ? action.type : null;
}
